import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/shadcn/button'

const useCopy = () => {
  const [copied, setCopied] = useState(false)

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error(`Could not copy ${label}`)
    }
  }

  return { copied, copy }
}

/** Single-line value with a copy affordance. For URLs and keys. */
export function CopyField({
  value,
  label,
  mono = true,
}: {
  value: string
  label: string
  mono?: boolean
}) {
  const { copied, copy } = useCopy()

  return (
    <div className="border-border flex items-center gap-2 rounded-md border px-3 py-2">
      <span className={`min-w-0 flex-1 truncate text-sm ${mono ? 'font-mono' : ''}`} title={value}>
        {value}
      </span>
      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0"
        aria-label={`Copy ${label}`}
        onClick={() => copy(value, label)}>
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}

/** Multi-line snippet (shell command, JSON config) with a copy affordance. */
export function CopyBlock({ value, label }: { value: string; label: string }) {
  const { copied, copy } = useCopy()

  return (
    <div className="relative">
      <pre className="bg-muted text-foreground overflow-x-auto rounded-md p-3 pr-12 text-xs leading-relaxed">
        <code>{value}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon-sm"
        className="absolute right-1.5 top-1.5"
        aria-label={`Copy ${label}`}
        onClick={() => copy(value, label)}>
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}
